import { ANSWERABLE_SEVERITIES, ADVICE_STATUS, SEVERITY } from 'constants/ChecklistConstants';

import { PAYMENT_TYPE, SETTLED_PAYMENT_STATUS } from 'constants/PaymentConstants';
import { getAdvices, groupAdvicesByPayerId } from 'services/ChecklistUtils';
import { computeValueWithoutVAT } from 'services/VATUtils';

import { computeAdviceTotal, computeAdviceTotalWithVat } from '../utils/InterventionPriceUtils';

export const computeItemTotalWithVat = ({ advices = [] }, vat) =>
  advices.reduce((acc, advice) => {
    const { severity } = advice;
    const canAdd = ANSWERABLE_SEVERITIES.includes(severity);
    return canAdd ? acc + computeAdviceTotalWithVat(advice, vat) : acc;
  }, 0);

export const computePendingItemTotalWithVat = ({ advices = [] }, vat) =>
  advices.reduce((acc, advice) => {
    const { severity, status } = advice;
    const canAdd = ANSWERABLE_SEVERITIES.includes(severity) && status === ADVICE_STATUS.PENDING;
    return canAdd ? acc + computeAdviceTotalWithVat(advice, vat) : acc;
  }, 0);

const computeTotalPendingChecklistPrice = ({ items = [] }, vat) =>
  items.reduce((acc, item) => acc + computePendingItemTotalWithVat(item, vat), 0);

export const computePendingChecklistsPrice = (checklists, vat) =>
  checklists.reduce((acc, checklist) => acc + computeTotalPendingChecklistPrice(checklist, vat), 0);

/**
 * Return the collection of ids used as `originId`. Means that they have been duplicated.
 * @param {import('modules/checklists/types/checklist').Checklist} checklist
 * @return {Array<string>}
 */
export const getDuplicatedAdvices = checklist =>
  getAdvices(checklist).reduce((acc, { originId }) => {
    if (originId) {
      acc.push(originId);
    }
    return acc;
  }, []);

/**
 * @param {Array<import("modules/checklists/types/working/checklist-advice-form").ChecklistAdviceForm>} advices
 * @param {number} vat
 * @param {import('modules/interventions/types/interfaces/payment').Payment[]} payments
 * @param {Array<import("@arhs-fleetback/types/entities/checklists").ChecklistAdviceStatus>?} totalExcludedStatus
 * @return {{
 *  total: number,
 *  totalWithVAT: number,
 *  depositPaid: number,
 *  depositPaidWithVAT: number,
 *  remainingAmount: number,
 *  remainingAmountWithVAT: number,
 *  paid: number,
 *  paidWithVAT: number,
 * }}
 */
export const getPrices = (advices, vat, payments = [], totalExcludedStatus = [ADVICE_STATUS.REFUSED]) => {
  const { depositPaidWithVAT, paidWithVAT } = payments.reduce((acc, payment) => {
    if (!SETTLED_PAYMENT_STATUS.includes(payment.status)) {
      return acc;
    }

    if (payment.type === PAYMENT_TYPE.FULL) {
      acc.paidWithVAT += payment.amount + acc.depositPaidWithVAT;
      acc.depositPaidWithVAT = 0;
    }

    if (payment.type === PAYMENT_TYPE.DEPOSIT) {
      acc.depositPaidWithVAT += payment.amount;
    }

    return acc;
  }, { depositPaidWithVAT: 0, paidWithVAT: 0 });

  const depositPaid = computeValueWithoutVAT(depositPaidWithVAT, vat);
  const paid = computeValueWithoutVAT(paidWithVAT, vat);

  return advices.reduce(
    (acc, advice) => {
      if (advice.severity !== SEVERITY.OFFERED) {
        const price = computeAdviceTotal(advice, vat);
        const priceWithVAT = computeAdviceTotalWithVat(advice, vat);

        if (!totalExcludedStatus.includes(advice.status)) {
          acc.total += price;
          acc.totalWithVAT += priceWithVAT;
        }

        acc.remainingAmount = acc.total - acc.paid - acc.depositPaid;
        acc.remainingAmountWithVAT = acc.totalWithVAT - acc.paidWithVAT - acc.depositPaidWithVAT;
      }
      return acc;
    },
    {
      total: 0,
      totalWithVAT: 0,
      depositPaid,
      depositPaidWithVAT,
      remainingAmount: 0,
      remainingAmountWithVAT: 0,
      paid,
      paidWithVAT,
    }
  );
};

export const formatGroupedPayerAdvices = (allAdvices, { vat, payments, totalExcludedStatus, translate, payers = [] }) =>
  groupAdvicesByPayerId(allAdvices, { translate, payers }).reduce(
    (acc, [payerId, advices]) => ({
      ...acc,
      [payerId]: {
        advices,
        ...getPrices(advices, vat, payments, totalExcludedStatus),
      },
    }),
    {}
  );
